import React, { MouseEvent, ReactNode, useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import favIcon from "../../assets/favIcon.svg";
import accountIcon from "../../assets/customerNavbarIcons/accountIcon.svg";
import shopIcon from "../../assets/customerNavbarIcons/shopIcon.svg";
import myOrdersIcon from "../../assets/myOrdersIcon.png";
import { ReactComponent as PermIdentityOutlinedIcon } from "../../assets/homePage/drop-profile.svg";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import NotificationsIcon from "../../assets/customerNavbarIcons/BellIcon.svg";
import NotificationsHighlightedIcon from "../../assets/customerNavbarIcons/BellIcon-Highlighted.svg";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import "./NavBar.css";
import { Skeleton } from "@mui/lab";
import ledgerIcon from "../../assets/customerNavbarIcons/ledger.svg";
import { useRole } from "../../context/RoleContext";
import { routes } from "../../routes/routes";
import prebookIcon from "../../assets/customerNavbarIcons/prebook.svg";
import { useQueryClient } from "@tanstack/react-query";
import NotificationsDropdown from "../notifications/NotificationsDropdown";
import { useAppDispatch } from "../../redux/hooks";
import { setNotifications } from "../../redux/features/slices/notificationSlice";
import { useNotifications } from "../../queries/notifications";
import { logoutWithBackend } from "../../api/loginAPI";
import { AuthApiService } from "../../service/authService";

interface NavbarProps {
  children?: ReactNode;
  logo?: string;
}
const Navbar = ({ children, logo }: NavbarProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const navigate = useNavigate();
  const cartItems = useSelector((state: RootState) => state.cart.items);
  const notifications = useSelector((state: RootState) => state.notifications);
  const [notificationMenuOpen, setNotificationMenuOpen] = useState(false);
  const { role, isRoleLoading } = useRole();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const customerId = localStorage.getItem("customerId") || "";
  const { data: backendNotifications } = useNotifications(customerId);

  useEffect(() => {
    if (backendNotifications?.notifications) {
      dispatch(
        setNotifications({
          notifications: backendNotifications.notifications,
          count: backendNotifications.count,
        })
      );
    }
  }, [backendNotifications, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: Event) => {
      if (notificationMenuOpen) {
        const target = event.target as HTMLElement;
        if (
          !target.closest(".notifications-dropdown") &&
          !target.closest(".navLinks")
        ) {
          setNotificationMenuOpen(false);
        }
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [notificationMenuOpen]);

  // Show loading state while role is being determined
  if (isRoleLoading) {
    return <div>Loading...</div>;
  }

  const handleNavigateToLedger = () => {
    navigate(`${role}/ledgers`);
    handleMenuClose();
  };
  const handleProfileMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleNavigateToOrders = () => {
    navigate(`${role}/my-orders`);
    handleMenuClose();
  };

  const handleNavigateToPrebookings = () => {
    navigate(`${role}/pre-bookings`);
    handleMenuClose();
  };

  const handleNavigateToProfile = () => {
    navigate(`${role}/customer-profile`);
    handleMenuClose();
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (target: string) => {
    if (target === "notifications") {
      setNotificationMenuOpen(!notificationMenuOpen);
      return;
    }
    navigate(`${role}/${target}`);
  };

  const handleLogout = () => {
    queryClient.clear();
    setAnchorEl(null);
    logoutWithBackend()
      .catch((err) => console.error("Logout failed:", err))
      .finally(() => {
        AuthApiService.logout()
          .catch((err) => console.error("Logout failed:", err))
          .finally(() => {
            navigate(`/${routes.public.signIn}`, {
              replace: true,
              state: { fromApp: true },
            });
          });
      });
  };

  const handleFeedback = () => {
    window.open("https://tally.so/r/J9epXR", "_blank");
    handleMenuClose();
  };

  const dropdownIconStyle = { width: "16px", height: "16px" };
  const dropDownItems = [
    {
      icon: <PermIdentityOutlinedIcon style={dropdownIconStyle} />,
      text: "Profile",
      onClick: handleNavigateToProfile,
    },
    {
      icon: (
        <img
          src={ledgerIcon}
          alt="Ledger Icon"
          width={dropdownIconStyle.width}
          height={dropdownIconStyle.height}
        />
      ),
      text: "Ledger",
      onClick: handleNavigateToLedger,
    },
    {
      icon: (
        <img
          src={myOrdersIcon}
          alt="My Orders"
          width={dropdownIconStyle.width}
          height={dropdownIconStyle.height}
        />
      ),
      text: "My Orders",
      onClick: handleNavigateToOrders,
    },
    {
      icon: (
        <img
          src={prebookIcon}
          alt="Prebooking Icon"
          width={dropdownIconStyle.width}
          height={dropdownIconStyle.height}
        />
      ),
      text: "Pre-Bookings",
      onClick: handleNavigateToPrebookings,
    },
    {
      icon: <FeedbackOutlinedIcon style={dropdownIconStyle} />,
      text: "Feedback",
      onClick: handleFeedback,
    },
    {
      icon: <LogoutOutlinedIcon style={dropdownIconStyle} />,
      text: "Log Out",
      onClick: handleLogout,
    },
  ];

  return (
    <div>
      <AppBar
        position="fixed"
        color="default"
        elevation={4}
        sx={{
          backgroundColor: "#fff",
          fontFamily: "Poppins, sans-serif",
          boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25) !important",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          top: 0,
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            paddingX: "25px !important",
          }}
        >
          <Typography
            variant="h6"
            noWrap
            sx={{
              display: "flex",
              alignItems: "center",
              marginRight: "20px",
            }}
          >
            <NavLink to={`${role}/home`} style={{ textDecoration: "none" }}>
              {!logo ? (
                <Skeleton
                  variant="rectangular"
                  sx={{
                    height: "30px",
                  }}
                />
              ) : (
                <img
                  src={logo}
                  alt="Logo"
                  style={{
                    height: "30px",
                    width: "auto",
                    maxWidth: "100%",
                    display: "block",
                    objectFit: "contain",
                    cursor: "pointer",
                  }}
                />
              )}
            </NavLink>
          </Typography>

          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <div className="navLinksPlain" onClick={() => handleClick("home")}>
              <p style={{ color: "#808080" }}>Home</p>
            </div>
            <div
              className="navLinksPlain"
              onClick={() => handleClick("products")}
            >
              <p style={{ color: "#808080" }}>Products</p>
            </div>
            <div
              className="navLinksPlain"
              onClick={() => handleClick("help-support")}
            >
              <p style={{ color: "#808080" }}>Support</p>
            </div>

            <div className="navLinks" onClick={() => handleClick("cart")}>
              {cartItems && cartItems.length > 0 && (
                <Typography className="cartCount">
                  {cartItems.length}
                </Typography>
              )}
              <img className="iconStyle" src={shopIcon} alt="shop" />
            </div>

            <div className="navLinks" onClick={() => handleClick("wishlist")}>
              <img className="iconStyle" src={favIcon} alt="favourite" />
            </div>
            {role === "maltora-customer" && (
              <div
                className="navLinks"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick("notifications");
                }}
                style={{ position: "relative", cursor: "pointer" }}
              >
                {(() => {
                  const unreadCount = backendNotifications?.count || 0;

                  return (
                    unreadCount > 0 && (
                      <Typography className="cartCount">
                        {unreadCount}
                      </Typography>
                    )
                  );
                })()}
                {notificationMenuOpen ? (
                  <img
                    src={NotificationsHighlightedIcon}
                    alt="notifications"
                    className="iconStyle"
                    style={{
                      pointerEvents: "none",
                    }}
                  />
                ) : (
                  <img
                    src={NotificationsIcon}
                    alt="notifications"
                    className="iconStyle"
                    style={{
                      pointerEvents: "none",
                    }}
                  />
                )}
                {notificationMenuOpen && (
                  <div
                    className="notifications-dropdown"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <NotificationsDropdown
                      notifications={notifications.items}
                      customerId={customerId}
                      onClose={() => setNotificationMenuOpen(false)}
                    />
                  </div>
                )}
              </div>
            )}
            <div className="navLinks" onClick={handleProfileMenuOpen}>
              <img className="iconStyle" src={accountIcon} alt="profile" />
              <strong>
                <KeyboardArrowDownOutlinedIcon
                  sx={{
                    fontSize: "inherit",
                    position: "relative",
                    top: "3px",
                  }}
                />
              </strong>
            </div>
          </Box>
        </Toolbar>

        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          MenuListProps={{
            role: "menu",
          }}
          sx={{
            transform: "translateX(6px)",
            ".css-1toxriw-MuiList-root-MuiMenu-list": {
              outline: 0,
              fontFamily: "Poppins, sans-serif",
              borderRadius: "10px",
              border: "1px solid #64748B",
            },
            ".MuiPaper-root": {
              boxShadow: "none",
            },
            ".MuiMenuItem-root": {
              padding: "8px 15px",
              marginX: "6px",
              "&:hover": {
                borderRadius: "4px",
              },
            },
          }}
        >
          {dropDownItems.map((item, idx) => (
            <React.Fragment key={item.text}>
              <MenuItem onClick={item.onClick} role="menuitem">
                {item.icon}
                <Typography sx={{ fontSize: "14px", paddingLeft: "8px" }}>
                  {item.text}
                </Typography>
              </MenuItem>
              {idx < dropDownItems.length - 1 && (
                <Divider sx={{ margin: "4px 0" }} />
              )}
            </React.Fragment>
          ))}
        </Menu>
      </AppBar>

      {children}
    </div>
  );
};

export default Navbar;
